import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type {
  ContactDetail,
  ContactListItem,
  ContactSearchResult,
  CreateContactPayload,
  PaginatedResponse,
  UpdateContactPayload,
} from "../types";

const CONTACTS_KEY = ["contacts"] as const;

export function useContactList(
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    companyId?: string;
  } = {},
): UseQueryResult<PaginatedResponse<ContactListItem>> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...CONTACTS_KEY, "list", params, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedResponse<ContactListItem>>("/contacts", {
        token: accessToken,
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          companyId: params.companyId,
        },
      }),
  });
}

export function useContact(id: string): UseQueryResult<ContactDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...CONTACTS_KEY, "detail", id, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: async () => {
      const res = await apiFetch<{ contact: ContactDetail }>(
        `/contacts/${id}`,
        { token: accessToken },
      );
      return res.contact;
    },
  });
}

export function useContactSearch(
  q: string,
  enabled = true,
): UseQueryResult<{ data: ContactSearchResult[] }> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...CONTACTS_KEY, "search", q, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && enabled,
    queryFn: () =>
      apiFetch<{ data: ContactSearchResult[] }>("/contacts/search", {
        token: accessToken,
        query: { q },
      }),
  });
}

export function useCreateContact() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateContactPayload) =>
      apiFetch<{ contact: ContactDetail }>("/contacts", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...CONTACTS_KEY] });
    },
  });
}

export function useUpdateContact(id: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateContactPayload) =>
      apiFetch<{ contact: ContactDetail }>(`/contacts/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...CONTACTS_KEY] });
    },
  });
}

export function useDeleteContact() {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/contacts/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...CONTACTS_KEY] });
    },
  });
}
